import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Check, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtEUR, EXPENSE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const empty = { fornecedor: "", amount: "", due_date: "", entity: "", reference: "", scope: "pessoal", status: "pendente" };

export default function Invoices() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const today = new Date().toISOString().slice(0, 10);

  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices"],
    queryFn: () => api.get("/invoices").then((r) => r.data),
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.get("/accounts").then((r) => r.data),
  });

  const save = async () => {
    if (!form.fornecedor.trim()) return toast.error("Indique o fornecedor");
    try {
      await api.post("/invoices", {
        ...form,
        amount: form.amount ? parseFloat(String(form.amount).replace(",", ".")) : null,
        due_date: form.due_date || null,
      });
      toast.success("Fatura adicionada");
      setOpen(false); setForm(empty); qc.invalidateQueries();
    } catch (err) { toast.error(apiError(err)); }
  };

  const updateDue = async (inv, due_date) => {
    await api.put(`/invoices/${inv.id}`, { ...inv, due_date: due_date || null });
    toast.success("Data atualizada");
    qc.invalidateQueries();
  };

  const pay = async (inv) => {
    const acc = accounts[0]?.id;
    await api.post(`/invoices/${inv.id}/pay`, null, { params: acc ? { account_id: acc } : {} });
    toast.success("Fatura marcada como paga");
    qc.invalidateQueries();
  };

  const del = async (id) => {
    if (!window.confirm("Apagar fatura?")) return;
    await api.delete(`/invoices/${id}`);
    qc.invalidateQueries();
  };

  const pendentes = invoices.filter((i) => i.status === "pendente");
  const pagas = invoices.filter((i) => i.status === "pago");

  const dueBadge = (inv) => {
    if (!inv.due_date) return <Badge variant="outline">sem data</Badge>;
    const overdue = inv.due_date < today;
    const soon = inv.due_date <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
    if (overdue) return <Badge className="bg-[#C75D42] text-white">vencida</Badge>;
    if (soon) return <Badge className="bg-[#D9A566] text-white">a vencer</Badge>;
    return null;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">Faturas a pagar</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-invoice-btn"><Plus size={16} className="mr-1" /> Nova fatura</Button>
          </DialogTrigger>
          <DialogContent data-testid="invoice-dialog">
            <DialogHeader><DialogTitle>Nova fatura</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Fornecedor</Label><Input value={form.fornecedor} onChange={(e) => setForm({ ...form, fornecedor: e.target.value })} placeholder="EDP, NOS…" data-testid="invoice-fornecedor" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Valor (€)</Label><Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="invoice-amount" /></div>
                <div><Label>Data limite</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Entidade</Label><Input value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} data-testid="invoice-entity" /></div>
                <div><Label>Referência</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="invoice-reference" /></div>
              </div>
              <div>
                <Label>Âmbito</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="invoice-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pessoal">Pessoal</SelectItem>
                    <SelectItem value="empresa">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="invoice-save">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2" data-testid="invoices-pending">
        {pendentes.length === 0 && <p className="text-sm text-muted-foreground">Sem faturas pendentes.</p>}
        {pendentes.map((inv) => (
          <div key={inv.id} className="bg-card border border-border rounded-lg p-4" data-testid={`invoice-${inv.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{inv.fornecedor}</span>
                  {dueBadge(inv)}
                  {inv.scope === "empresa" && <Badge variant="outline" className="text-[#C75D42] border-[#C75D42]">empresa</Badge>}
                </div>
                {(inv.entity || inv.reference) && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {inv.entity && `Ent. ${inv.entity}`} {inv.reference && `· Ref. ${inv.reference}`}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground">Vencimento:</span>
                  <Input
                    type="date"
                    value={inv.due_date || ""}
                    onChange={(e) => updateDue(inv, e.target.value)}
                    className="h-8 w-40 text-xs"
                    data-testid={`invoice-due-inline-${inv.id}`}
                  />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-lg font-bold" style={{ color: EXPENSE }}>{inv.amount ? fmtEUR(inv.amount) : "—"}</div>
                <div className="flex gap-1 mt-2 justify-end">
                  <button onClick={() => pay(inv)} className="text-[#2E7D5C] p-1" title="Marcar como paga" data-testid={`invoice-pay-${inv.id}`}><Check size={18} /></button>
                  <button onClick={() => del(inv.id)} className="text-muted-foreground hover:text-[#C75D42] p-1" data-testid={`invoice-delete-${inv.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pagas.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground mb-2">Pagas</h2>
          <div className="space-y-1">
            {pagas.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between bg-secondary/40 rounded-md px-3 py-2 text-sm">
                <span className="line-through text-muted-foreground">{inv.fornecedor}</span>
                <div className="flex items-center gap-3">
                  <span>{inv.amount ? fmtEUR(inv.amount) : "—"}</span>
                  <button onClick={() => del(inv.id)} className="text-muted-foreground"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
